import type { Vehicle } from "@/types/fleet";

/**
 * Upstream tags `driver_id` rather than sending a bare id:
 *   `id:{xxx}` — a `ClientDriver` id, to be resolved to a name.
 *   `c:{code}` — a raw identifier read off the device (an RFID fob with no driver record).
 */
export type DriverRef =
  | { kind: "driver"; driverId: string }
  | { kind: "code"; code: string };

const DRIVER_PREFIX = "id:";
const CODE_PREFIX = "c:";

/**
 * Anything untagged — including a bare id — means no driver, not a driver we failed to parse.
 *
 * Some upstream writes re-tag a value that is already tagged (`"id:" + "id:{driverId}"`), so a
 * stored reference can carry a stack of them. Every tag is stripped; the *innermost* one — the last
 * reached — decides the kind, since the outer ones were prepended afterwards. A value tagged so
 * many times that nothing is left is no driver at all rather than an unresolvable one.
 */
export function parseDriverRef(raw?: string | null): DriverRef | null {
  let value: string = raw?.trim() ?? "";
  let kind: DriverRef["kind"] | null = null;

  for (;;) {
    if (value.startsWith(DRIVER_PREFIX)) {
      kind = "driver";
      value = value.slice(DRIVER_PREFIX.length).trim();
    } else if (value.startsWith(CODE_PREFIX)) {
      kind = "code";
      value = value.slice(CODE_PREFIX.length).trim();
    } else {
      break;
    }
  }

  if (!kind || !value || isTagShard(value)) return null;
  return kind === "driver" ? { kind, driverId: value } : { kind, code: value };
}

/**
 * Truncation cuts mid-tag as readily as mid-id, so a reference eaten to the end can leave a shard
 * of the tag itself (`"id"`, `"i"`, `"c"`) where the id should be. That is nothing, not an id.
 */
function isTagShard(value: string): boolean {
  return DRIVER_PREFIX.startsWith(value) || CODE_PREFIX.startsWith(value);
}

/**
 * Route history reports a `drivers` array — a route can have more than one. Unparseable entries
 * are dropped and duplicates collapsed, so the same driver reported twice on one route shows once.
 */
export function parseDriverRefs(raw?: readonly string[] | null): DriverRef[] {
  if (!Array.isArray(raw)) return [];

  const byKey = new Map<string, DriverRef>();
  for (const entry of raw) {
    const ref = parseDriverRef(entry);
    if (!ref) continue;
    const key = formatDriverRef(ref);
    if (!byKey.has(key)) byKey.set(key, ref);
  }
  return [...byKey.values()];
}

/** Inverse of {@link parseDriverRef}. The empty string is the wire value that clears an assignment. */
export function formatDriverRef(ref: DriverRef | null): string {
  if (!ref) return "";
  return ref.kind === "driver" ? `${DRIVER_PREFIX}${ref.driverId}` : `${CODE_PREFIX}${ref.code}`;
}

/**
 * Re-tagging (see {@link parseDriverRef}) overflows the upstream column, which truncates from the
 * right — so what survives is a *prefix* of the real driver id. `canonicalIdByFragment`, built by
 * `buildDriverIdFragmentMap`, maps those prefixes back to the whole id. A fragment it does not know,
 * and every `c:` code — which has no record to match against — is returned unchanged.
 */
export function canonicalizeDriverRef(
  ref: DriverRef,
  canonicalIdByFragment: Map<string, string>,
): DriverRef {
  if (ref.kind === "code") return ref;
  const canonical = canonicalIdByFragment.get(ref.driverId);
  return canonical && canonical !== ref.driverId ? { kind: "driver", driverId: canonical } : ref;
}

/**
 * Dedupes after canonicalising, so the same driver recorded at several truncation depths on one
 * route collapses to a single ref — both in the cell and in the per-driver aggregates keyed off it.
 * The array itself is returned untouched when nothing resolved, keeping memoized consumers stable.
 */
export function canonicalizeDriverRefs(
  refs: readonly DriverRef[],
  canonicalIdByFragment: Map<string, string>,
): DriverRef[] {
  const byKey = new Map<string, DriverRef>();
  let changed = false;

  for (const ref of refs) {
    const canonical = canonicalizeDriverRef(ref, canonicalIdByFragment);
    if (canonical !== ref) changed = true;
    const key = formatDriverRef(canonical);
    if (byKey.has(key)) changed = true;
    else byKey.set(key, canonical);
  }

  return changed ? [...byKey.values()] : (refs as DriverRef[]);
}

/** Rebuilt from the two `Vehicle` fields; a code wins if both are somehow set. */
export function vehicleDriverRef(vehicle: Pick<Vehicle, "driverId" | "driverCode">): DriverRef | null {
  if (vehicle.driverCode) return { kind: "code", code: vehicle.driverCode };
  if (vehicle.driverId) return { kind: "driver", driverId: vehicle.driverId };
  return null;
}

export type DriverDisplay =
  /** `id:` resolved against the drivers list. */
  | { kind: "name"; text: string }
  /** `id:` matched nothing — a dangling assignment, shown so it is not mistaken for "unassigned". */
  | { kind: "unknown"; text: string }
  /** `c:` — no record to look up, so the raw code is the display. */
  | { kind: "code"; text: string };

/**
 * The single source of truth for what a driver reference shows. Every renderer — the tracking list
 * row, the map popup, the settings table, the history table — goes through this so they cannot
 * drift apart.
 */
export function resolveRefDisplay(
  ref: DriverRef,
  driverNamesById: Map<string, string>,
): DriverDisplay {
  if (ref.kind === "code") return { kind: "code", text: ref.code };

  const name = driverNamesById.get(ref.driverId);
  return name ? { kind: "name", text: name } : { kind: "unknown", text: ref.driverId };
}

/** The single-driver case a vehicle carries, in its two-field form. */
export function resolveDriverDisplay(
  vehicle: Pick<Vehicle, "driverId" | "driverCode">,
  driverNamesById: Map<string, string>,
): DriverDisplay | null {
  const ref = vehicleDriverRef(vehicle);
  return ref ? resolveRefDisplay(ref, driverNamesById) : null;
}

/** The multi-driver case a route carries. */
export function resolveDriverDisplays(
  refs: readonly DriverRef[] | undefined,
  driverNamesById: Map<string, string>,
): DriverDisplay[] {
  return (refs ?? []).map((ref) => resolveRefDisplay(ref, driverNamesById));
}

/** Plain text for exports, which have no way to render an icon. */
export function formatDriverDisplaysText(displays: readonly DriverDisplay[]): string {
  return displays.map((display) => display.text).join(", ");
}

/** Stable key for change detection, so a code and a name of the same text are still distinct. */
export function driverDisplayKey(display: DriverDisplay | null): string {
  return display ? `${display.kind}:${display.text}` : "";
}
